import { AxiosRequestConfig, AxiosResponse } from 'axios';
import Axios from 'axios';
import dayjs from 'dayjs';

import CustomHttpClient from '../customHttpClient';
import AuthenticationService from '../authentication';

import { getError, getUserInfo, getUserInfoAlter } from '../../utils';
import localStorageHelper from '../../utils/localStorageHelper';

import {
  Claim,
  GetClaimsFilter,
  GetClaimsListServiceResponse,
} from '../../interfaces/services/claim';
import {
  Base64,
  DuplicateClaimParams,
  DuplicateClaimResponse,
  GetAmountRequest,
  GetAmountResponse,
  GetPolicyByPolicySearch,
  GetPreComplaintNumberResponse,
  GetVoucherRequest,
  ORDER_BY,
  SendNewComplainServiceRequestParams,
  SendNewComplainRVServiceRequestParams,
  SendNewComplainServiceResponse,
  UserInformation,
} from '../../interfaces';
import { GetClaimsInspectionsListServiceResponse } from '../../interfaces/services/claimInspection';
import { GetClaimsPaymentsListServiceResponse } from '../../interfaces/services/claimPayment';
import { GetClaimsStatusListServiceResponse } from '../../interfaces/services/claimStatus';
import { DocumentUploadServiceRequestParams, DocumentUploadServiceResponse } from '../../interfaces/services/post-send-claim-documents-service';
import { InsertRelatedDocumentServiceRequest } from '../../interfaces/services/post-insert-related-document-service';
import { ComplaintPdfDataResponse } from '../../interfaces/services/get-complaint-pdf-service';
import { CompensationReceiptDataResponse } from '../../interfaces/services/compensation-receipt-pdf-service';
import { GetVoucherNumberResponse } from '../../interfaces/services/get-voucher-number-service';
import userHelper from '../../utils/userHelper';
import { FranchiseLetterRequestParams, FranchiseLetterServiceResponse } from '../../interfaces/services/post-franchise-letter-service';
import { PolicyEndorsementAssetsResponse } from '../../interfaces/services/possessionsByEndorsementRV';
import { InsertDamagedObjectsRequestParams, InsertDamagedObjectsResponse } from '../../interfaces/services/insertDamagedObjects-service';
import { GetIssuedObjectsRequestParams, GetIssuedObjectsResponse } from '../../interfaces/services/issuedObjects-service';
import { InsertLifeClaimHeaderRequest, InsertLifeClaimHeaderResponse } from '../../interfaces/services/insertLifeClaim-service';
import { InsertDamagedRequest } from '../../interfaces/services/insert-damaged-services';
import { GetCoverageCertificateResponse, GetCoverageCertificateRequest } from '../../interfaces/services/get-coverage-certificate-service';

const customHttpClientMastersClaimScope = CustomHttpClient({ baseUrl: 'siniestros', scope: 'Siniestros' });
const customHttpClientPolicy = CustomHttpClient({ baseUrl: 'polizas', scope: 'Polizas' });
const customHttpClientClaimRVScope = CustomHttpClient({ baseUrl: 'siniestrosvida', scope: 'Siniestrosvida' });

const customHttpClient = CustomHttpClient({ baseUrl: 'productores' });
const customHttpClientPolicies = CustomHttpClient({
  baseUrl: 'polizas',
  scope: 'Polizas',
});

const ClaimService = {
  async getClaims(
    filters: GetClaimsFilter,
    lastClaimNumber: number,
    operationNumberClaim: number,
    branchClaim: number
  ): Promise<GetClaimsListServiceResponse> {
    const url = 'listadoDeSiniestros';
    const params = { ...getUserInfo() };

    try {
      if (!this.hasAppliedFilters(filters)) {
        if (lastClaimNumber) {
          const params: AxiosRequestConfig = { params: { numeroSiniestro: lastClaimNumber, numeroOperacionSiniestro: operationNumberClaim, rama: branchClaim } };
          const response = await customHttpClientMastersClaimScope.get("consultaDetalleSiniestro", params);
          const claimData = response.data;
          const claimResponse = this.buildClaim(claimData);
          
          const claimsList: GetClaimsListServiceResponse = {
            siniestros: [claimResponse]
          };
          return claimsList;
        } else {
          return { siniestros: [] };
        }

      } else {
        const response = await customHttpClient.get(url, { params });
        let siniestrosResponse = response.data;
        siniestrosResponse.siniestros =  this.filterAndSortClaims(siniestrosResponse.siniestros, filters);

        return siniestrosResponse;
      }

    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getClaimPayments(
    branch: string,
    claimId: string
  ): Promise<GetClaimsPaymentsListServiceResponse> {
    const url = 'pagoSiniestros';
    const params = {
      ...getUserInfo(),
      rama: branch,
      siniestro: claimId,
    };

    try {
      const response = await customHttpClient.get(url, { params });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getClaimInspections(
    branch: string,
    claimId: string
  ): Promise<GetClaimsInspectionsListServiceResponse> {
    const url = 'inspeccionesDeSiniestro';
    const params = {
      ...getUserInfo(),
      rama: branch,
      siniestro: claimId,
    };

    try {
      const response = await customHttpClient.get(url, { params });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getClaimStatus(
    branch: string,
    claimId: string
  ): Promise<GetClaimsStatusListServiceResponse> {
    const url = 'estadosDeUnSiniestro';
    const params = {
      ...getUserInfo(),
      rama: branch,
      siniestro: claimId,
    };

    try {
      const response = await customHttpClient.get(url, { params });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPolicyByPatentSearch(
    patentNumber: string,
    occurrenceDate: string,
    occurrenceHour: string
  ): Promise<GetPolicyByPolicySearch> {
    const url = 'identificaPolizaPorPatente';
    const params = {
      patente: patentNumber,
      fechaOcurrencia: occurrenceDate,
      horaOcurrencia: occurrenceHour,
    };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPolicyByPolicySearch(
    branch: string,
    policyNumber: string,
    occurrenceDate: string,
    occurrenceHour: string
  ): Promise<GetPolicyByPolicySearch> {
    const url = 'identificaPolizaPorRamaPoliza';
    const params = {
      rama: branch,
      numeroPoliza: policyNumber,
      fechaOcurrencia: occurrenceDate,
      horaOcurrencia: occurrenceHour,
    };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getSearchPolicyByProducer(
    patentNumber: string,
    occurrenceDate: string,
    occurrenceHour: string,
    branch: string,
    policy: string,
    filter: string
  ): Promise<AxiosResponse> {
    const url = 'buscarPolizaVigente';
    const params = {
      ...getUserInfo(),
      patente: patentNumber,
      fechaOcurrencia: occurrenceDate,
      horaOcurrencia: occurrenceHour,
      rama: branch,
      poliza: policy,
      filtro: filter
    };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  //DSRV-30
    async getCurrentPolicy(
    patentNumber: string,
    occurrenceDate: string,
    occurrenceHour: string,
    branch: string,
    policy: string,
    filter: string
  ): Promise<AxiosResponse> {
    const url = 'obtenerPolizaVigente';
    const params = {
      ...getUserInfo(),
      patente: patentNumber,
      fech: occurrenceDate,
      hora: occurrenceHour,
      rama: branch,
      poliza: policy,
      marc: filter
    };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response;
    } catch (error) {
      throw error;
    }
  },

  async sendNewComplaint(
    requestParams: SendNewComplainServiceRequestParams
  ): Promise<AxiosResponse<SendNewComplainServiceResponse>> {
    const url = 'altaSiniestrosAutos';

    try {
      const response =
        await customHttpClientMastersClaimScope.post<SendNewComplainServiceResponse>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

    async sendNewComplaintRV(requestParams: SendNewComplainRVServiceRequestParams
  ): Promise<AxiosResponse<SendNewComplainServiceResponse>> {
    const url = 'altaSiniestrosRV';

    try {
      const response =
        await customHttpClientMastersClaimScope.post<SendNewComplainServiceResponse>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getDuplicateClaim(
    requestParams: DuplicateClaimParams
  ): Promise<DuplicateClaimResponse> {
    const url = 'obtenerSiniestrosDuplicados';
    const params = { ...requestParams };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVoucher(type: string, params: GetVoucherRequest): Promise<Base64> {
    const url = `obtenerVoucher?tipo=${type}`;

    try {
      const response = await customHttpClientMastersClaimScope.post(url, {
        ...params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getAmounts(data: GetAmountRequest): Promise<GetAmountResponse> {
    const url = 'obtenerImportes';
    const params = { ...getUserInfo(), ...data };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getPreComplaintNumber(): Promise<GetPreComplaintNumberResponse> {
    const url = 'obtenerNumeroDePredenuncia';
    const params = { ...getUserInfo() };

    try {
      const response = await customHttpClientMastersClaimScope.get(url, {
        params,
      });

      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getVoucherNumber(voucherType: string): Promise<GetVoucherNumberResponse> {
    const url = 'obtenerNumeroDeVoucher';
    const { codigoDeEmpresa, codigoDeSucursal }: UserInformation = userHelper.getUser()!
    try {
      const response = await customHttpClientMastersClaimScope.post(url, {
        empresa: codigoDeEmpresa,
        sucursal: codigoDeSucursal,
        tipoVoucher: voucherType
        });
      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async sendClaimDocuments(
    requestParams: DocumentUploadServiceRequestParams
  ): Promise<AxiosResponse<DocumentUploadServiceResponse>> {
    const url = 'uploadClaimDocument';

    try {
      const response =
        await customHttpClient.post<DocumentUploadServiceResponse>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw error;
    }
  },

  async insertRelatedClaimDocuments(
    requestParams: InsertRelatedDocumentServiceRequest
  ): Promise<AxiosResponse<void>> {
    const url = 'insertarDocumentosRelacionados';

    try {
      const response =
        await customHttpClient.post<void>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async postInsertComplaint(
    branch: string,
    policyNumber: string,
    domain: string,
    dateOccurrence: string,
    timeOccurrence: string,
    causeNumber: string,
    complaintNumber: string
  ): Promise<AxiosResponse<void>> {
    const url = 'insertarPredenuncia';
    const timeOccurrenceFull = dayjs(timeOccurrence, 'HH:mm:ss');
    const params = {
      ...getUserInfo(),
      rama: branch,
      poliza: policyNumber,
      patente: domain,
      fechaOcurrencia: dateOccurrence.split('-').reverse().join(''), //Formato a respetar: YYYYMMDD: (ej: 20250130)
      horaOcurrencia: timeOccurrenceFull.format('HHmm'),
      causa: causeNumber,
      numeroPredenuncia: complaintNumber,
    };

    try {
      const response = await customHttpClient.post(url, params);
      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getComplaintPdfData(
    branch: string,
    claimNumber: string
  ): Promise<ComplaintPdfDataResponse> {
    const url = 'denunciaPdfData';
    const params = {
      ...getUserInfo(),
      rama: branch,
      siniestro: claimNumber,
    };

    try {
      const response = await customHttpClientMastersClaimScope.post(url, params);
      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  async getCompensationReceiptData(paymentOrderNumber: string): Promise<CompensationReceiptDataResponse> {
    const url = 'reciboIndemnizacionData';
    const params = {
      ...getUserInfo(),
      numeroOrdenDePago: paymentOrderNumber,
    };
    try {
      const response = await customHttpClientMastersClaimScope.post(url, params);
      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },
  hasAppliedFilters(filters: GetClaimsFilter): boolean {
    const safeTrim = (val: any) => typeof val === 'string' ? val.trim() : '';

    const hasClaimUpRange =
      Array.isArray(filters.claimUp) &&
      filters.claimUp.length === 2 &&
      filters.claimUp[0] != null &&
      filters.claimUp[1] != null;

    const hasClaimDateRange =
      Array.isArray(filters.claimDate) &&
      filters.claimDate.length === 2 &&
      filters.claimDate[0] != null &&
      filters.claimDate[1] != null;

    return !!(
      safeTrim(filters.insured) ||
      safeTrim(filters.policy) ||
      safeTrim(filters.claimNumber) ||
      safeTrim(filters.claimAsset) ||
      hasClaimUpRange ||
      hasClaimDateRange
    );
  },
  buildClaim(data: any): Claim {
  return {
    asegurado: data.asegurado?.nombre ?? '',
    bienSiniestrado: data.vehiculo?.dominio ?? '',
    causa: data.descripcionCausa ?? '',
    claseDocumental: '',
    denunciaPdf: '',
    rama: String(data.codigoRama ?? '0'),
    documentoPredenuncia: '',
    enJuicio: '',
    poliza: data.poliza?.numeroPoliza ?? '',
    estado: data.descripcionEstado ?? '',
    fechaDenuncia: data.fechaDenuncia ?? '',
    fechaSiniestro: data.fechaOcurrencia ?? '',
    importePago: '.00',
    nroSiniestro: data.numeroSiniestro ?? '',
    numeroDeVoucher: '',
    tipoDeVoucher: ''
  };
},
async sendFranchiseLetter(
    requestParams: FranchiseLetterRequestParams
  ): Promise<AxiosResponse<FranchiseLetterServiceResponse>> {
    const url = 'solicitaCartaFranquicia';

    try {
      const response =
        await customHttpClient.post<FranchiseLetterServiceResponse>(
          url,
          requestParams
        );

      return response;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  //DSRV-26
  async getInsuredAssetsByEndorsement(
    branch: string,
    policyNumber: string,
    articulo: string,
    superpoliza: string,
    suplementoPoliza: string,
    suplementoSuperpoliza: string,
  ): Promise<PolicyEndorsementAssetsResponse> {
    const url = 'obtenerbienesporendoso';
    const params = {
      ...getUserInfo(),
      rama: branch,
      nroPoliza: policyNumber,
      articulo: articulo,
      superpoliza: superpoliza,
      suplementoSuperpoliza: suplementoSuperpoliza,
      suplementoOperacion: suplementoPoliza,
    };
    try {
      const response = await customHttpClientPolicy.get(url, { params });
      return response.data;
    } catch (error) {
      throw new Error(getError(error));
    }
  },

  //DSRV-33 -- TODO SE USA EN JAVA
  async insertDamagedObjects(requestParams: InsertDamagedObjectsRequestParams): Promise<AxiosResponse<InsertDamagedObjectsResponse>> {
  const url = 'insertaObjetosSiniestrados';
  try {
    const response =
      await customHttpClientMastersClaimScope.post<InsertDamagedObjectsResponse>(
        url,
        requestParams
      );

    return response;
  } catch (error) {
    throw new Error(getError(error));
  }
},

//DSRV-34
async getIssuedObjects(params: GetIssuedObjectsRequestParams): Promise<AxiosResponse<GetIssuedObjectsResponse>> {
  const url = 'obtieneObjetosEmitidos';
  try {
    const response =
      await customHttpClientPolicy.get<GetIssuedObjectsResponse>(url, { params } );
    return response;
  } catch (error) {
    throw new Error(getError(error));
  }
},

//DSRV-32 -- TODO SE USA EN JAVA
async insertaCaratulaVida(requestParams: InsertLifeClaimHeaderRequest): Promise<AxiosResponse<InsertLifeClaimHeaderResponse>> {
  const url = 'insertaCaratulaVida';
  try {
    const response =
      await customHttpClient.post<InsertLifeClaimHeaderResponse>(
        url,
        requestParams
      );
    return response;
  } catch (error) {
    throw new Error(getError(error));
  }
},


filterAndSortClaims(claims: Claim[], filters: GetClaimsFilter): Claim[] {
  claims = claims.filter(obj => Object.keys(obj).length > 0);
  return claims
    .filter((siniestro: Claim) =>
      (filters.insured?.length ? siniestro.asegurado.toLowerCase().includes(filters.insured.toLowerCase()) : true) &&
      (filters.policy?.length ? siniestro.poliza.includes(filters.policy) : true) &&
      (filters.claimNumber?.length ? siniestro.nroSiniestro.includes(filters.claimNumber) : true) &&
      (filters.claimAsset?.length ? siniestro.bienSiniestrado.toLowerCase().includes(filters.claimAsset.toLowerCase()) : true) &&
      (filters.claimUp?.length === 2
        ? dayjs(siniestro.fechaDenuncia).isBetween(
          dayjs(filters.claimUp[0]),
          dayjs(filters.claimUp[1]),
          'day',
          '[]'
        )
        : true) &&
      (filters.claimDate?.length === 2
        ? dayjs(siniestro.fechaSiniestro).isBetween(
          dayjs(filters.claimDate[0]),
          dayjs(filters.claimDate[1]),
          'day',
          '[]'
        )
        : true)
    )
    .sort((a: Claim, b: Claim) => {
      switch (filters.orderBy) {
        case ORDER_BY.ALPHABETICAL_ORDER:
          return a.asegurado.localeCompare(b.asegurado);
        case ORDER_BY.OLDEST_FIRST:
          return dayjs(a.fechaSiniestro).diff(dayjs(b.fechaSiniestro));
        case ORDER_BY.MOST_RECENT_FIRST:
        default:
          return dayjs(b.fechaSiniestro).diff(dayjs(a.fechaSiniestro));
      }
    });
},

async getCoverageCertificate(
  request: GetCoverageCertificateRequest
): Promise<AxiosResponse<GetCoverageCertificateResponse>> {
  // Determinar endpoint según rama
  const getEndpoint = (rama: string) => {
    switch(rama) {
      case "3": // AUTOMOVILES
        return 'impresionDePoliza/ConstanciaDeCoberturaVehiculo';
      default:
        throw new Error(`Rama desconocida para Constancia de Cobertura: ${rama}`);
    }
  };

  const endpoint = getEndpoint(request.rama);
  const params = {
    ...getUserInfo(),
    rama: request.rama,
    poliza: request.poliza,
    siniestro: request.siniestro,
  };

  try {
    const response = await customHttpClientPolicies.get(endpoint, { params });
    return response;
  } catch (error: any) {
    throw new Error(getError(error));
  }
},

async insertDamagedWheels(requestParams: InsertDamagedRequest): Promise<AxiosResponse<void>> {
  const url = 'daniosVehiculoRuedas';
  try {
    const response =
      await customHttpClientMastersClaimScope.post<void>(
        url,
        requestParams
      );

    return response;
  } catch (error) {
    throw new Error(getError(error));
  }
},

async insertDamagedGlass(requestParams: InsertDamagedRequest): Promise<AxiosResponse<void>> {
  const url = 'daniosVehiculoCristales';
  try {
    const response =
      await customHttpClientMastersClaimScope.post<void>(
        url,
        requestParams
      );

    return response;
  } catch (error) {
    throw new Error(getError(error));
  }
},

};

export default ClaimService;
